import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AdminHeader from "../../components/AdminHeader";
import { FullPageLoading } from "../../components/Loading";
import { Eyebrow, HeadingItalic } from "../../components/design";
import { adminListUsers, type AdminUserRow } from "../../lib/api";
import { formatTimestamp } from "../../lib/format";

export default function AdminDashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r = await adminListUsers({
          search: debouncedSearch || undefined,
          limit: 200,
        });
        if (cancelled) return;
        setUsers(r.users || []);
        setTotal(r.total || 0);
        setError(null);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || "Fehler beim Laden.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [debouncedSearch]);

  const rows = useMemo(() => users, [users]);

  return (
    <div className="paper-bg" style={{ minHeight: "100vh" }}>
      <AdminHeader />
      <main
        className="desk-container-admin"
        style={{
          padding: "36px 22px 60px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "flex-end",
            justifyContent: "space-between",
            gap: 16,
            marginBottom: 22,
          }}
        >
          <div>
            <Eyebrow>Admin</Eyebrow>
            <HeadingItalic size={34}>
              Erzähler-<em>Verwaltung</em>.
            </HeadingItalic>
            <p
              style={{
                fontFamily: "var(--f-body)",
                fontStyle: "italic",
                fontSize: 14.5,
                color: "var(--ink-faint)",
                margin: 0,
              }}
            >
              {total > 0
                ? `${total} Erzähler-Profil${total === 1 ? "" : "e"} insgesamt`
                : "Keine Erzähler vorhanden."}
            </p>
          </div>
          <Link to="/admin/users/new" className="btn-primary">
            + Neuen Erzähler manuell anlegen
          </Link>
        </div>

        <div
          style={{
            background: "var(--paper-50)",
            border: "1px solid rgba(43,26,15,0.10)",
            borderRadius: 14,
            padding: 8,
            marginBottom: 18,
          }}
        >
          <input
            type="search"
            placeholder="Suche: Telefonnummer, Name, E-Mail …"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input"
            style={{ border: "none", background: "transparent" }}
          />
        </div>

        {error && (
          <div
            role="alert"
            style={{
              background: "rgba(107,32,24,0.08)",
              border: "1px solid rgba(107,32,24,0.25)",
              borderLeft: "3px solid var(--oxblood)",
              borderRadius: 4,
              padding: "12px 16px",
              color: "var(--oxblood-d)",
              marginBottom: 18,
            }}
          >
            {error}
          </div>
        )}

        {loading ? (
          <FullPageLoading />
        ) : rows.length === 0 ? (
          <div
            style={{
              background: "var(--paper-50)",
              border: "1px solid rgba(43,26,15,0.10)",
              borderRadius: 14,
              padding: "32px 22px",
              textAlign: "center",
              fontFamily: "var(--f-body)",
              fontStyle: "italic",
              color: "var(--ink-faint)",
            }}
          >
            Keine Treffer.
          </div>
        ) : (
          <UsersTable rows={rows} />
        )}
      </main>
    </div>
  );
}

function UsersTable({ rows }: { rows: AdminUserRow[] }) {
  return (
    <div
      style={{
        background: "var(--paper-50)",
        border: "1px solid rgba(43,26,15,0.10)",
        borderRadius: 14,
        overflow: "hidden",
        boxShadow:
          "0 1px 0 rgba(43,26,15,0.04), 0 14px 24px -8px rgba(43,26,15,0.14)",
      }}
    >
      <div style={{ overflowX: "auto" }}>
        <table
          style={{
            minWidth: "100%",
            borderCollapse: "collapse",
            fontFamily: "var(--f-body)",
            fontSize: 14,
          }}
        >
          <thead>
            <tr
              style={{
                background: "var(--paper-200)",
                borderBottom: "1px solid rgba(43,26,15,0.10)",
              }}
            >
              <Th>Telefonnummer</Th>
              <Th>Erzähler</Th>
              <Th>Auftraggeber</Th>
              <Th>E-Mail</Th>
              <Th>Letzter Anruf</Th>
              <Th>Fortschritt</Th>
              <Th>Erstellt</Th>
              <Th>Welcome</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u, i) => (
              <tr
                key={u.phone}
                style={{
                  borderTop:
                    i === 0 ? "none" : "1px solid rgba(43,26,15,0.08)",
                  cursor: "pointer",
                  transition: "background 100ms",
                }}
                onMouseEnter={(e) =>
                  ((e.currentTarget as HTMLElement).style.background =
                    "var(--paper-200)")
                }
                onMouseLeave={(e) =>
                  ((e.currentTarget as HTMLElement).style.background =
                    "transparent")
                }
              >
                <Td>
                  <Link
                    to={`/admin/users/${encodeURIComponent(u.phone)}`}
                    style={{
                      fontFamily: "var(--f-body)",
                      color: "var(--oxblood)",
                      textDecoration: "none",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {u.phone}
                  </Link>
                </Td>
                <Td>
                  <Link
                    to={`/admin/users/${encodeURIComponent(u.phone)}`}
                    style={{
                      fontFamily: "var(--f-display)",
                      fontWeight: 500,
                      fontSize: 17,
                      color: "var(--ink)",
                      textDecoration: "none",
                    }}
                  >
                    {u.interviewee_name || "—"}
                  </Link>
                </Td>
                <Td>
                  <span style={{ color: "var(--ink-soft)" }}>
                    {u.orderer_name || "—"}
                  </span>
                </Td>
                <Td>
                  <span
                    style={{
                      color: "var(--ink-soft)",
                      maxWidth: 220,
                      display: "inline-block",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      verticalAlign: "bottom",
                    }}
                  >
                    {u.orderer_email}
                  </span>
                </Td>
                <Td>
                  {u.lastCallAt ? (
                    <span style={{ color: "var(--ink-soft)", whiteSpace: "nowrap" }}>
                      {formatTimestamp(u.lastCallAt)}
                    </span>
                  ) : (
                    <DotPill color="var(--ink-faint)" label="noch keiner" />
                  )}
                </Td>
                <Td>
                  <ProgressCell value={u.chapter_count} total={13} />
                </Td>
                <Td>
                  <span style={{ color: "var(--ink-faint)" }}>
                    {formatTimestamp(u.createdAt)}
                  </span>
                </Td>
                <Td>
                  <DotPill
                    color={
                      u.welcome_email_sent ? "var(--sage)" : "var(--ochre)"
                    }
                    label={u.welcome_email_sent ? "gesendet" : "offen"}
                  />
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Th({
  children,
  align = "left",
}: {
  children: React.ReactNode;
  align?: "left" | "right";
}) {
  return (
    <th
      className="t-caps"
      style={{
        textAlign: align,
        padding: "12px 14px",
        color: "var(--ink-faint)",
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align = "left",
}: {
  children: React.ReactNode;
  align?: "left" | "right";
}) {
  return (
    <td
      style={{
        textAlign: align,
        padding: "12px 14px",
        verticalAlign: "middle",
        color: "var(--ink)",
      }}
    >
      {children}
    </td>
  );
}

function ProgressCell({ value, total }: { value: number; total: number }) {
  const pct = total > 0 ? Math.min(100, Math.round((value / total) * 100)) : 0;
  const color =
    pct === 0
      ? "var(--ink-faint)"
      : pct < 33
      ? "var(--ochre)"
      : pct < 75
      ? "var(--ochre)"
      : "var(--sage)";
  return (
    <div style={{ minWidth: 120 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontFamily: "var(--f-body)",
          fontSize: 14,
          color: "var(--ink-soft)",
        }}
      >
        <span style={{ minWidth: 44, whiteSpace: "nowrap" }}>
          {value} / {total}
        </span>
        <span
          aria-hidden
          style={{
            flex: 1,
            height: 6,
            background: "rgba(43,26,15,0.08)",
            borderRadius: 999,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <span
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: `${pct}%`,
              background: color,
              borderRadius: 999,
              transition: "width 200ms ease-out",
            }}
          />
        </span>
      </div>
    </div>
  );
}

function DotPill({ color, label }: { color: string; label: string }) {
  return (
    <span
      className="t-caps"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        background: "var(--paper-200)",
        color: "var(--ink-soft)",
        padding: "3px 9px",
        borderRadius: 999,
        border: "1px solid rgba(43,26,15,0.08)",
      }}
    >
      <span
        aria-hidden
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: color,
          display: "inline-block",
        }}
      />
      {label}
    </span>
  );
}
